import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Copy, ArrowLeft, FileDown, Pencil } from "lucide-react";
import {
  useTemplates, useTemplateItems, useCreateTemplate, useUpdateTemplate, useDeleteTemplate,
  useCreateTemplateItem, useUpdateTemplateItem, useDeleteTemplateItem,
  useEvents, useCreateTemplateFromEvent,
  CATEGORIES, CATEGORY_COLORS, PRIORITY_COLORS, formatCategory
} from "@/hooks/useChecklistsData";

const TemplatesTab: React.FC = () => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [fromEventOpen, setFromEventOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<any>(null);
  const [form, setForm] = useState({ name: "", description: "", is_default: false });
  const [itemForm, setItemForm] = useState({ category: "other", task: "", priority: "medium", days_before_event: 7, sort_order: 0, notes: "" });
  const [fromEventForm, setFromEventForm] = useState({ eventId: "", name: "", description: "" });

  const { data: templates = [] } = useTemplates();
  const { data: items = [] } = useTemplateItems(selectedTemplateId);
  const { data: events = [] } = useEvents();

  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const createItem = useCreateTemplateItem();
  const updateItem = useUpdateTemplateItem();
  const deleteItem = useDeleteTemplateItem();
  const createFromEvent = useCreateTemplateFromEvent();

  const handleCreateTemplate = () => {
    createTemplate.mutate(form);
    setCreateOpen(false);
    setForm({ name: "", description: "", is_default: false });
  };

  const handleEditTemplate = () => {
    if (!editTemplate) return;
    updateTemplate.mutate({ id: editTemplate.id, updates: form });
    setEditTemplate(null);
    setForm({ name: "", description: "", is_default: false });
  };

  const handleDuplicate = (t: any) => {
    createTemplate.mutate({ name: `${t.name} (Copy)`, description: t.description, is_default: false });
  };

  const handleAddItem = () => {
    if (!selectedTemplateId || !itemForm.task.trim()) return;
    createItem.mutate({ ...itemForm, template_id: selectedTemplateId });
    setAddItemOpen(false);
    setItemForm({ category: "other", task: "", priority: "medium", days_before_event: 7, sort_order: 0, notes: "" });
  };

  const handleCreateFromEvent = () => {
    if (!fromEventForm.eventId || !fromEventForm.name.trim()) return;
    createFromEvent.mutate(fromEventForm);
    setFromEventOpen(false);
    setFromEventForm({ eventId: "", name: "", description: "" });
  };

  // Detail view
  if (selectedTemplateId) {
    const template = templates.find((t: any) => t.id === selectedTemplateId);
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSelectedTemplateId(null)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h2 className="text-lg font-semibold">{template?.name}</h2>
          {template?.is_default && <Badge variant="secondary">Default</Badge>}
        </div>
        {template?.description && <p className="text-sm text-muted-foreground">{template.description}</p>}

        <div className="flex gap-2">
          <Button size="sm" onClick={() => setAddItemOpen(true)} className="gap-1"><Plus className="h-4 w-4" /> Add Item</Button>
        </div>

        <div className="rounded-md border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Days Before</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No items yet. Add tasks to this template.</TableCell></TableRow>
              ) : items.map((item: any) => (
                <TableRow key={item.id}>
                  <TableCell><Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[item.category]}`}>{formatCategory(item.category)}</Badge></TableCell>
                  <TableCell className="font-medium">{item.task}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[item.priority]}`}>{item.priority}</Badge></TableCell>
                  <TableCell>{item.days_before_event}d</TableCell>
                  <TableCell>{item.sort_order}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{item.notes || "—"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteItem.mutate(item.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Add item modal */}
        <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Template Item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Category</Label>
                <Select value={itemForm.category} onValueChange={v => setItemForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{formatCategory(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Task *</Label><Input value={itemForm.task} onChange={e => setItemForm(p => ({ ...p, task: e.target.value }))} /></div>
              <div><Label>Priority</Label>
                <Select value={itemForm.priority} onValueChange={v => setItemForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Days Before Event</Label><Input type="number" value={itemForm.days_before_event} onChange={e => setItemForm(p => ({ ...p, days_before_event: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label>Sort Order</Label><Input type="number" value={itemForm.sort_order} onChange={e => setItemForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={itemForm.notes} onChange={e => setItemForm(p => ({ ...p, notes: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
              <Button onClick={handleAddItem} disabled={!itemForm.task.trim()}>Add Item</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Template list view
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => { setForm({ name: "", description: "", is_default: false }); setCreateOpen(true); }} className="gap-1">
          <Plus className="h-4 w-4" /> Create Template
        </Button>
        <Button variant="outline" onClick={() => setFromEventOpen(true)} className="gap-1">
          <FileDown className="h-4 w-4" /> Create from Event
        </Button>
      </div>

      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No templates yet.</TableCell></TableRow>
            ) : templates.map((t: any) => (
              <TableRow key={t.id} className="cursor-pointer" onClick={() => setSelectedTemplateId(t.id)}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{t.description || "—"}</TableCell>
                <TableCell>{t.is_default && <Badge variant="secondary">Default</Badge>}</TableCell>
                <TableCell className="flex gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditTemplate(t); setForm({ name: t.name, description: t.description || "", is_default: t.is_default }); }}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDuplicate(t)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteTemplate.mutate(t.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create template modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_default} onCheckedChange={v => setForm(p => ({ ...p, is_default: v }))} /><Label>Default Template</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={!form.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit template modal */}
      <Dialog open={!!editTemplate} onOpenChange={() => setEditTemplate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_default} onCheckedChange={v => setForm(p => ({ ...p, is_default: v }))} /><Label>Default Template</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTemplate(null)}>Cancel</Button>
            <Button onClick={handleEditTemplate} disabled={!form.name.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create from event modal */}
      <Dialog open={fromEventOpen} onOpenChange={setFromEventOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Template from Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Event *</Label>
              <Select value={fromEventForm.eventId} onValueChange={v => setFromEventForm(p => ({ ...p, eventId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select event..." /></SelectTrigger>
                <SelectContent>{events.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Template Name *</Label><Input value={fromEventForm.name} onChange={e => setFromEventForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={fromEventForm.description} onChange={e => setFromEventForm(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFromEventOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFromEvent} disabled={!fromEventForm.eventId || !fromEventForm.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesTab;
